/**
 * Local view-model types for the suggested-outcomes feature. The wire
 * types come from `@morfi/backend-sdk-v2/client` once codegen runs.
 *
 * The "effective" helpers pick `creator_edited_*` overlay fields if
 * present, otherwise the user-supplied originals. Used by the row
 * renderer + the creator review modal.
 */

export type VoteKind = 'up' | 'down';
export type SuggestionStatus = 'active' | 'promoting' | 'promoted';
export type GenerationMode = 'ai' | 'manual';

/**
 * Wire shape of the embedded user identity on every suggestion row. Mirrors
 * the backend's canonical `userIdentitySchema`. The SDK row type
 * `GetOutcomeSuggestionsRow.suggester` is structurally identical — kept
 * here as a separate alias so callers don't have to reach across the SDK
 * type boundary for a primitive identity-shape.
 */
export interface SuggesterIdentity {
  userId: string | null;
  walletAddress: string;
  displayName: string | null;
  handle: string | null;
  avatarUrl: string | null;
  avatarNsfw?: boolean;
  avatarPreferences?: { rasterise: boolean };
  connectedProviders?: ReadonlyArray<{
    provider: string;
    username?: string | null;
    displayName?: string | null;
  }>;
}

export interface SuggestionWire {
  id: string;
  marketPubkey: string;
  proposalPubkey: string;
  suggesterPubkey: string;
  suggester: SuggesterIdentity;
  label: string;
  ticker: string;
  imageUrl: string;
  reason: string | null;
  upvotes: number;
  downvotes: number;
  score: number;
  status: SuggestionStatus;
  generationMode: GenerationMode;
  creatorEditedLabel: string | null;
  creatorEditedTicker: string | null;
  creatorEditedImageUrl: string | null;
  promotedOutcomePubkey: string | null;
  outcomeIndex: number | null;
  promotedAt: string | null;
  createdAt: string;
  /** The viewer's current vote on this suggestion. `null` means no vote.
   *  Backend computes per-request from the auth session. */
  viewerKind: VoteKind | null;
}

export function effectiveLabel(s: SuggestionWire): string {
  return s.creatorEditedLabel ?? s.label;
}
export function effectiveTicker(s: SuggestionWire): string {
  return s.creatorEditedTicker ?? s.ticker;
}
export function effectiveImageUrl(s: SuggestionWire): string {
  return s.creatorEditedImageUrl ?? s.imageUrl;
}
